import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import Row from "./Row";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 75, 100];

const TableWrapper = (props) => {
  const { columns, data, resize, noPagination, pageSize, selectedPerPage, rowCount, pageNo, onPageChange, ...rest } = props;

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  const descendingComparator = (a, b, orderBy) => {
    const first = typeof a[orderBy] === "string" ? a[orderBy].toLocaleLowerCase() : a[orderBy];
    const second = typeof b[orderBy] === "string" ? b[orderBy].toLocaleLowerCase() : b[orderBy];

    if (second < first) return -1;
    if (second > first) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    selectedPerPage(parseInt(event.target.value, 10));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const EnhancedTableHead = ({ order, orderBy, onRequestSort, resize }) => {
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    const renderColumnWithoutSortIcon = (headCell) => {
      return <div>{headCell.headerName}</div>;
    };

    return (
      <TableHead className={`${(rest.module === "hotel-config" || rest.module === "partner-config" || rest.module === "third-party-hotel-config") ? "hotel-table" : ""}`}>
        <TableRow>
          {columns.map((headCell) => (
            <TableCell
              key={headCell.field}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.field ? order : false}
              style={{ padding: '6px', whiteSpace: 'nowrap' }}
            >
              <div className={resize ? "resizeBox" : ""}>
                {headCell.disableSortIcon ? (
                  renderColumnWithoutSortIcon(headCell)
                ) : (
                  <TableSortLabel
                    active={orderBy === headCell.field}
                    direction={orderBy === headCell.field ? order : "asc"}
                    onClick={createSortHandler(headCell.field)}
                  >
                    <span className="fnt-bold">
                      {headCell.headerName}
                    </span>
                  </TableSortLabel>
                )}
              </div>
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      </TableHead>
    );
  };

  EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    resize: PropTypes.bool.isRequired,
  };

  return (
    <div className="table-wrapper">
      <TableContainer>
        <Table stickyHeader>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data?.length}
            resize={resize}
          />
          {data?.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography variant="subtitle2" className="text-center my-3">
                    {rest.noDataAvailable===undefined?"No Data Available...":rest.noDataAvailable!==""?rest.noDataAvailable :''}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody className='hotel-table'>
              {stableSort(data, getComparator(order, orderBy))?.map((row) => (
                <Row
                  key={row.sid}
                  columns={columns}
                  row={row}
                  data={data}
                  {...rest}
                />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {!noPagination && (
        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={rowCount || 0}
          rowsPerPage={rowsPerPage}
          page={pageNo - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="pagination"
        />
      )}
    </div>
  );
};

TableWrapper.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      disablePadding: PropTypes.bool,
      disableSortIcon: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  resize: PropTypes.bool,
  noPagination: PropTypes.bool,
  pageSize: PropTypes.number,
  selectedPerPage: PropTypes.func.isRequired,
  rowCount: PropTypes.number,
  pageNo: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

TableWrapper.defaultProps = {
  resize: false,
  noPagination: false,
  pageSize: 10,
  rowCount: 0,
};

export default TableWrapper;
